import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useMeContext } from 'modules/me';

export const SD_UNSELECTED_KEY = 'sd_unselected_ids';

type ReturnType = {
  addUnselectedEntries: (ids: string[]) => void;
  removeUnselectedEntries: (ids: string[]) => void;
  getUnselectedEntries: () => Record<string, string>;
};

export const useSmartEntryUnselectState = (): ReturnType => {
  const me = useMeContext();
  const key = useMemo(() => `${me?.id}_${SD_UNSELECTED_KEY}`, [me?.id]);
  return {
    addUnselectedEntries: useCallback(
      (ids: string[]) => addUnselectedEntries({ key, ids }),
      [key]
    ),
    removeUnselectedEntries: useCallback(
      (ids: string[]) => removeUnselectedEntries({ key, ids }),
      [key]
    ),
    getUnselectedEntries: useCallback(() => getItem(key), [key])
  };
};

const getItem = (key: string): Record<string, string> => {
  try {
    const value = sessionStorage.getItem(key);
    return JSON.parse(value ?? '{}');
  } catch {
    return {};
  }
};

const setItem = (key: string, value: Record<string, string>) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

const addUnselectedEntries = ({
  key,
  ids
}: {
  key: string;
  ids: string[];
}): void => {
  if (!ids.length) {
    return;
  }
  const oldValues = getItem(key);
  const timestamp = DateTime.local().toISO();

  const newValues = ids.reduce((acc, id) => {
    acc[id] = timestamp;
    return acc;
  }, {} as Record<string, string>);
  setItem(key, { ...oldValues, ...newValues });
};

const removeUnselectedEntries = ({
  key,
  ids
}: {
  key: string;
  ids: string[];
}): void => {
  if (!ids.length) {
    return;
  }
  const values = getItem(key);

  ids.forEach((id) => delete values[id]);
  setItem(key, values);
};
